import { ArrowLeft, CheckCircle2, KeyRound, Loader2, Mail } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '@/api/client';
import EarningsDisclaimer from '@/components/EarningsDisclaimer';

/**
 * ForgotPasswordPage — sends a password reset link to the supplied email.
 *
 * The backend always returns 200 (anti-enumeration) so the success screen
 * we display is identical whether or not the email is registered.
 */
const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const _navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      toast.error('Enter the email on your account');
      return;
    }
    setBusy(true);
    try {
      await api.post('/api/auth/password-reset/request', { email: trimmed });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Could not send link: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen flex flex-col items-center justify-center px-4 py-10 gap-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md sc-glass-strong rounded-2xl p-6 md:p-8 relative overflow-hidden" style={{ borderColor: 'rgba(255,182,18,0.32)' }}>
        <div
          className="absolute -top-20 -right-20 w-72 h-72 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(255,182,18,0.18), transparent 60%)',
          }}
          aria-hidden="true"
        />
        <div className="relative z-10">
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)] hover:text-[#FFC20E] mb-3"
            data-testid="forgot-back-link"
          >
            <ArrowLeft className="w-3 h-3" /> Back to login
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center border"
              style={{
                background: 'rgba(255,182,18,0.06)',
                borderColor: 'rgba(255,182,18,0.32)',
              }}
              aria-hidden="true"
            >
              <KeyRound className="w-5 h-5 text-[#FFC20E]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Reset your password</div>
              <h1 className="font-display font-bold text-xl mt-0.5">Forgot password</h1>
            </div>
          </div>

          {sent ? (
            <div data-testid="forgot-success-panel">
              <div
                className="flex items-center gap-3 rounded-xl p-4 border mb-3"
                style={{
                  background: 'rgba(16,185,129,0.08)',
                  borderColor: 'rgba(16,185,129,0.32)',
                }}
              >
                <CheckCircle2 className="w-5 h-5 text-[#10B981] shrink-0" />
                <div className="text-[13px] text-[var(--sc-text-bright)] leading-snug">
                  If that email is registered, a reset link is on its way. The link expires in 60 minutes.
                </div>
              </div>
              <Link
                to="/login"
                className="sc-btn-white inline-flex items-center justify-center gap-2 w-full"
                style={{ padding: '12px 22px', fontSize: 13 }}
                data-testid="forgot-back-to-login"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-[12px] text-[var(--sc-text-dim)] leading-relaxed">
                Enter the email on your account. We'll send a single-use link that lets you set a new password.
              </p>
              <div>
                <label htmlFor="reset-email" className="block text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-1.5">
                  Email
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 sc-input flex items-center gap-2">
                    <Mail className="w-4 h-4 text-[var(--sc-text-mute)]" />
                    <input
                      id="reset-email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="flex-1 bg-transparent outline-none"
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
              </div>
              <button type="submit" disabled={busy} className="sc-btn-white w-full" style={{ padding: '12px 22px', fontSize: 13 }} data-testid="forgot-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                Send reset link
              </button>
            </form>
          )}
        </div>
      </div>
      <EarningsDisclaimer variant="compact" />
    </main>
  );
};

export default ForgotPasswordPage;
